import {UseQueryResult} from '@tanstack/react-query';
import {useEffect, useMemo, useState} from 'react';

import {DataLoadingSwitch} from '../../../../../../../component/loading-switch/loading-switch';
import {BarChart} from '../../../../../../../layout/chart/bar-chart/bar-chart';
import {BarDataSetType, BarDataType} from '../../../../../../../layout/chart/bar-chart/bar-chart-type';
import {LegendBox} from '../../../../../../../layout/chart/legend-box/legend-box';
import {LegendWrapper} from '../../../../../../../layout/chart/legend-wrapper/legend-wrapper';
import {getStripesPattern} from '../../../../../../../layout/chart/patterns/stripes';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {ReviewAspectsToneDistributionType} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {useReviewsAnalysisFilter, useReviewsAnalysisTooltipFormatter} from '../../../../reviews-analysis-hook';
import {getAspectsToneDistributionChartOptions} from '../aspects-helper';

import {aspectsToneDistributionColors, aspectsToneDistributionLangKeys} from './aspects-tone-distribution-const';
import {AspectsToneEnum} from './aspects-tone-type';
import * as styles from './aspects-tone-distribution-charts.scss';

type PropsType = {
    aspectsToneDistribution: UseQueryResult<ReviewAspectsToneDistributionType>;
};

export function AspectsToneDistributionChart({aspectsToneDistribution}: PropsType): JSX.Element {
    const {data, isLoading, isError} = aspectsToneDistribution;

    const isEmpty = useMemo(() => {
        return data?.aspectsTonality && data.aspectsTonality.length === 0;
    }, [data]);

    const {comparePeriodFilter} = useReviewsAnalysisFilter(false);
    const {getLocalizedString} = useLocale();

    const isCompareMode: boolean = useMemo(() => {
        return Boolean(comparePeriodFilter.startDate);
    }, [comparePeriodFilter.startDate]);

    const aspects = useMemo(() => {
        return (
            data?.aspectsTonality?.map((aspectData) => {
                return aspectData.aspect;
            }) || []
        );
    }, [data]);

    const [enabledDatasetsMap, setEnabledDatasetsMap] = useState<Record<AspectsToneEnum, boolean>>({
        [AspectsToneEnum.Positive]: true,
        [AspectsToneEnum.Negative]: true,
        [AspectsToneEnum.Neutral]: true,
    });
    const [compareDatasetsVisible, setCompareDatasetsVisible] = useState<boolean>(isCompareMode);

    useEffect(() => {
        setEnabledDatasetsMap({
            [AspectsToneEnum.Positive]: true,
            [AspectsToneEnum.Negative]: true,
            [AspectsToneEnum.Neutral]: true,
        });
    }, [aspects, data]);

    useEffect(() => {
        setCompareDatasetsVisible(isCompareMode);
    }, [isCompareMode]);

    const mainDatasets: Array<BarDataSetType> = useMemo(() => {
        return [
            ...(enabledDatasetsMap[AspectsToneEnum.Negative]
                ? [
                      {
                          stack: 'current',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEGATIVE'
                          ),
                          color: aspectsToneDistributionColors[AspectsToneEnum.Negative],
                          values:
                              data?.aspectsTonality.map((aspectData) => aspectData.currentPeriodData.negative) || [],
                      },
                  ]
                : []),
            ...(enabledDatasetsMap[AspectsToneEnum.Neutral]
                ? [
                      {
                          stack: 'current',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEUTRAL'
                          ),
                          color: aspectsToneDistributionColors[AspectsToneEnum.Neutral],
                          values: data?.aspectsTonality.map((aspectData) => aspectData.currentPeriodData.neutral) || [],
                      },
                  ]
                : []),
            ...(enabledDatasetsMap[AspectsToneEnum.Positive]
                ? [
                      {
                          stack: 'current',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__POSITIVE'
                          ),
                          color: aspectsToneDistributionColors[AspectsToneEnum.Positive],
                          values:
                              data?.aspectsTonality.map((aspectData) => aspectData.currentPeriodData.positive) || [],
                      },
                  ]
                : []),
        ];
    }, [data, enabledDatasetsMap, getLocalizedString]);

    const compareDatasets: Array<BarDataSetType> = useMemo(() => {
        if (!isCompareMode) {
            return [];
        }

        return [
            ...(enabledDatasetsMap[AspectsToneEnum.Negative]
                ? [
                      {
                          stack: 'compare',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEGATIVE'
                          ),
                          color: getStripesPattern(aspectsToneDistributionColors[AspectsToneEnum.Negative]),
                          values:
                              data?.aspectsTonality.map((aspectData) => aspectData.comparePeriodData?.negative || 0) ||
                              [],
                      },
                  ]
                : []),
            ...(enabledDatasetsMap[AspectsToneEnum.Neutral]
                ? [
                      {
                          stack: 'compare',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEUTRAL'
                          ),
                          color: getStripesPattern(aspectsToneDistributionColors[AspectsToneEnum.Neutral]),
                          values:
                              data?.aspectsTonality.map((aspectData) => aspectData.comparePeriodData?.neutral || 0) ||
                              [],
                      },
                  ]
                : []),
            ...(enabledDatasetsMap[AspectsToneEnum.Positive]
                ? [
                      {
                          stack: 'compare',
                          label: getLocalizedString(
                              'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__POSITIVE'
                          ),
                          color: getStripesPattern(aspectsToneDistributionColors[AspectsToneEnum.Positive]),
                          values:
                              data?.aspectsTonality.map((aspectData) => aspectData.comparePeriodData?.positive || 0) ||
                              [],
                      },
                  ]
                : []),
        ];
    }, [data, enabledDatasetsMap, isCompareMode, getLocalizedString]);

    const chartData: BarDataType = useMemo(() => {
        return {
            labels: aspects,
            datasets: [...mainDatasets, ...compareDatasets],
        };
    }, [aspects, compareDatasets, mainDatasets]);

    const getTooltipLabel = useReviewsAnalysisTooltipFormatter();

    return (
        <PageCard
            bodyClassName={styles.AspectsToneDistribution}
            title={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION" />}
        >
            <DataLoadingSwitch isEmpty={isEmpty} isError={isError} isLoading={isLoading}>
                <LegendWrapper>
                    {Object.values(AspectsToneEnum).map((aspectTone) => {
                        return (
                            <LegendBox
                                checked={enabledDatasetsMap[aspectTone]}
                                color={aspectsToneDistributionColors[aspectTone]}
                                key={aspectTone}
                                label={getLocalizedString(aspectsToneDistributionLangKeys[aspectTone])}
                                onChange={() =>
                                    setEnabledDatasetsMap((previousState) => {
                                        return {
                                            ...previousState,
                                            [aspectTone]: !previousState[aspectTone],
                                        };
                                    })
                                }
                            />
                        );
                    })}
                    {isCompareMode && (
                        <LegendBox
                            checked={compareDatasetsVisible}
                            key="compare"
                            label={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__COMPARE_PERIOD" />}
                            onChange={() => setCompareDatasetsVisible((previousState) => !previousState)}
                            stripes
                        />
                    )}
                </LegendWrapper>

                <div
                    className={styles.AspectsToneDistribution_chartWrapper}
                    style={{
                        ...(aspects.length > 0
                            ? {
                                  height: `${aspects.length * 50}px`,
                              }
                            : {
                                  height: '200px',
                              }),
                    }}
                >
                    <BarChart
                        data={chartData}
                        options={getAspectsToneDistributionChartOptions({
                            data: {
                                labels: chartData.labels,
                                datasets: mainDatasets,
                            },
                            ...(compareDatasetsVisible
                                ? {
                                      compareData: {
                                          labels: chartData.labels,
                                          datasets: compareDatasets,
                                      },
                                  }
                                : {}),
                            formatLabel: getTooltipLabel,
                        })}
                    />
                </div>
            </DataLoadingSwitch>
        </PageCard>
    );
}
